/**
 * アカウント → 料金プラン（縦串11、034、docs/26 §3。射場さん 2026-09-20 → #287、2026-11-01 射場さん ★5、docs/100・Figma「文言と画面の見直し（11/1）」⑤）。PC 専用（#185）
 *   見出しの行：戻る・「料金プラン」・？・右に「パソコン専用」のチップ
 *   1. 現在のプラン（白いカード）：左＝小さく「現在のプラン」・プラン名（28px 太字）＋状態チップ（無料期間／無料トライアル 残り n日／閲覧のみ）・月額と人数、
 *      右＝利用状況の4枚（利用人数・テンプレート・保存容量・AI読み取り（今月）。上限の無いものは「—」）、下＝注の1行と「お支払い方法登録」（#69 まで押せない）
 *   2. プラン変更：3枚（頭の帯＝選択中は主色の塗りに白字・他は見出しの地）。額・税込・項目の表・下げられない理由（朱の1行）・「選択中」か「{プラン}に変更」。
 *      無料のあいだの変更は お金がかからないので確認なし＋トーストの「元に戻す」（受注／失注と同じ作法）
 *   3. オプション：表（名前・額と税込・状態チップ 利用中／利用中（お試し）／未契約・申込／解約＝#69 まで押せない）。営業力は在庫の段を1行ずつ先頭に
 *   下の1行：21人以上と特定商取引法に基づく表記
 *   カードでのお支払い（PAY.JP）の受付が始まる前は、だれも制限しない。お支払い方法・お支払い履歴は #69
 */
import {
  HOME_MSG,
  BILLING_MSG,
  OPTION_STATE_LABEL,
  PLAN_PAGE_MSG as M,
  RECEIVED_READ_MSG,
  SCHEDULE_MSG,
  fillProduct,
  storageGbText,
  type OptionState,
  type Plan,
  type PlanPage,
} from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { api, messageOf } from "../api.js";
import { ME_KEY } from "../auth.js";
import { jmonthDay, trialLastDay } from "../components/Shell.jsx";
import { BackButton, useBackNav, usePageName } from "../components/BackButton.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { t } from "../labels.js";

const yen = (n: number) => n.toLocaleString("ja-JP");

/** 「{x}」の所に部品を入れる（文は契約に1か所＝リンクの言葉も契約から） */
function withLink(text: string, key: string, node: ReactNode): ReactNode {
  const at = text.indexOf(`{${key}}`);
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      {node}
      {text.slice(at + key.length + 2)}
    </>
  );
}

/** 利用状況の1枚（「利用人数 2 ／3人」） */
function UsageTile({ label, value, limit, testId }: { label: string; value: string; limit: string; testId: string }) {
  return (
    <div className="pp-tile" data-testid={testId}>
      <span className="pp-tile-l">{label}</span>
      <span className="pp-tile-v">
        <b className="num">{value}</b>
        <span className="pp-tile-lim">{limit}</span>
      </span>
    </div>
  );
}

/** オプションの表の1行 */
function OptionRow({ name, exTax, inTax, state, help, testId }: { name: string; exTax: number; inTax: number; state: OptionState; help?: ReactNode; testId: string }) {
  const on = state !== "off";
  return (
    <div className="pp-opt" role="row" data-testid={testId} data-state={state}>
      <span className="pp-opt-name" role="cell">
        {name}
        {help}
      </span>
      <span className="pp-opt-price" role="cell">
        <b className="num">{yen(exTax)}円</b>
        <span className="muted">{M.inTax(yen(inTax))}</span>
      </span>
      <span className="pp-opt-sp" aria-hidden="true" />
      <span role="cell">
        <span className={"chip pp-chip" + (on ? " is-on" : "")} data-testid={`${testId}-state`}>
          {OPTION_STATE_LABEL[state]}
        </span>
      </span>
      <span role="cell">
        {/* 申込・解約はお支払い方法の登録が始まってから（#69）。それまでは押せない */}
        <button type="button" className={on ? "btn text pp-opt-btn" : "btn tonal pp-opt-btn"} disabled title={M.optionsFoot}>
          {on ? M.cancel : M.apply}
        </button>
      </span>
    </div>
  );
}

export function AccountPlanPage() {
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const [error, setError] = useState<string | undefined>();
  const q = useQuery({ queryKey: ["account-plan"], queryFn: () => api<PlanPage>("GET", "/account/plan") });

  const change = useMutation({
    mutationFn: (v: { plan: Plan; undoTo?: Plan }) => api<PlanPage>("PUT", "/account/plan", { plan: v.plan }),
    onSuccess: (page, v) => {
      qc.setQueryData(["account-plan"], page);
      void qc.invalidateQueries({ queryKey: ME_KEY }); // メニューのプラン名
      void qc.invalidateQueries({ queryKey: ["estimate-templates"] }); // テンプレートの上限
      const label = page.plans.find((p) => p.current)?.label ?? "";
      const undoTo = v.undoTo;
      toast(BILLING_MSG.planChanged(label), undoTo ? { label: M.undo, onClick: () => change.mutate({ plan: undoTo }) } : undefined);
    },
    onError: (e) => setError(messageOf(e)),
  });

  // 戻る（#98、docs/37）：直前の画面があればそこ。無ければ ホーム
  const back = useBackNav({ to: "/home", label: HOME_MSG.menu });
  usePageName(M.title);
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  const page = q.data;
  const b = page.billing;
  const cur = page.plans.find((p) => p.current)!;
  const u = page.usage;
  const st = page.optionStates;

  const chip =
    b.phase === "prelaunch" ? (
      <span className="chip ai">{M.chipPrelaunch}</span>
    ) : b.phase === "trial" ? (
      <span className="chip ai">{M.chipTrial(b.daysLeft ?? 0)}</span>
    ) : (
      <span className="chip shu">{M.chipExpired}</span>
    );
  const note =
    b.phase === "prelaunch"
      ? b.liveFrom
        ? M.notePrelaunchDated(jmonthDay(b.liveFrom), page.trialDays)
        : M.notePrelaunch
      : b.phase === "trial"
        ? M.noteTrial(b.trialEndsAt ? trialLastDay(b.trialEndsAt) : "")
        : withLink(M.noteExpired(b.trialEndsAt ? trialLastDay(b.trialEndsAt) : ""), "export", <Link to="/settings/close">{M.exportLink}</Link>);

  return (
    <div className="planpg" data-testid="plan-page">
      <div className="pp-head">
        <BackButton back={back} />
        <h1>
          {M.title}
          <Help testId="help-plan" text={M.titleHelp} />
        </h1>
        <span className="sp" />
        <span className="chip pp-pconly">{M.pcOnly}</span>
      </div>

      <section className="pp-card pp-now" aria-labelledby="plan-now">
        <div className="pp-now-top">
          <div className="pp-now-main">
            <h2 id="plan-now" className="pp-now-l">
              {M.current}
            </h2>
            <p className="pp-now-name" data-testid="plan-current">
              <b>{cur.label}</b>
              <span data-testid="plan-phase">{chip}</span>
            </p>
            <p className="pp-now-price">{M.monthly(yen(cur.priceExTax), yen(cur.priceInTax), cur.seats)}</p>
          </div>
          <div className="pp-tiles" data-testid="plan-usage">
            <UsageTile label={M.usageSeats} value={String(u.seats)} limit={`／${cur.seats}人`} testId="plan-usage-seats" />
            <UsageTile label={M.usageTemplates} value={String(u.templates)} limit={`／${cur.templates}件`} testId="plan-usage-templates" />
            <UsageTile label={M.usageStorage} value={storageGbText(u.storageBytes)} limit={`／${cur.storageGb} GB`} testId="plan-usage-storage" />
            <UsageTile label={M.usageReads} value={String(u.readsUsed)} limit={u.readsLimit === null ? `／${M.noLimit}` : `／${u.readsLimit}通`} testId="plan-usage-reads" />
          </div>
        </div>
        <div className="pp-now-foot">
          <p className="pp-note" data-testid="plan-note">
            {note}
          </p>
          {/* お支払い方法の登録は #69（PAY.JP）。それまで押せない枠のボタン */}
          <button type="button" className="btn pp-pay" disabled title={M.payMethodSoon} data-testid="plan-pay">
            {M.payMethod}
          </button>
        </div>
      </section>

      <section className="pp-card" aria-labelledby="plan-change">
        <div className="pp-sechead">
          <h2 id="plan-change">
            {M.change}
            <Help testId="help-plan-change" text={t(M.changeHelp)} />
          </h2>
          <span className="sp" />
          <span className="pp-secnote">{M.changeNote}</span>
        </div>
        {page.canChange ? null : <p className="pp-note" data-testid="plan-admin-only">{BILLING_MSG.adminOnly}</p>}
        <div className="pp-plans">
          {page.plans.map((p) => (
            <div key={p.code} className={"pp-plan" + (p.current ? " is-selected" : "")} data-testid={`plan-${p.code}`}>
              <div className="pp-plan-band">
                <h3>{p.label}</h3>
                <span>{p.tagline}</span>
              </div>
              <div className="pp-plan-body">
                <p className="pp-plan-price">
                  <b className="num">{yen(p.priceExTax)}</b>
                  <span>{M.perMonth}</span>
                </p>
                <p className="pp-plan-tax">{M.inTax(yen(p.priceInTax))}</p>
                <dl className="pp-plan-rows">
                  <div>
                    <dt>{M.rowSeats}</dt>
                    <dd>{p.seats}人</dd>
                  </div>
                  <div>
                    <dt>{M.rowTemplates}</dt>
                    <dd>{p.templates}件</dd>
                  </div>
                  <div>
                    <dt>{M.rowStorage}</dt>
                    <dd>{p.storageGb} GB</dd>
                  </div>
                  <div data-testid={`plan-reads-${p.code}`}>
                    <dt>{M.rowReads}</dt>
                    <dd>{p.readsPerMonth}通</dd>
                  </div>
                  <div>
                    <dt>{M.rowHistory}</dt>
                    <dd>{p.revisionHistory ? M.yes : M.no}</dd>
                  </div>
                </dl>
                {p.blockedReason ? <p className="blocked">⚠ {p.blockedReason}</p> : null}
                {p.current ? (
                  <button type="button" className="btn pp-plan-btn" disabled>
                    {M.selected}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn primary pp-plan-btn"
                    disabled={!page.canChange || !!p.blockedReason || change.isPending}
                    onClick={() => {
                      setError(undefined);
                      change.mutate({ plan: p.code, undoTo: cur.code });
                    }}
                  >
                    {M.changeTo(p.label)}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <ErrorText>{error}</ErrorText>
      </section>

      {/* オプション（049、../eigyouryoku/docs/06 §2.2）：並びは両製品とも 在庫（営業力だけ・段ごとに1行）→ 帳票デザイン → 工程管理（スケジュール管理）→ AI書類読み取り
          （紹介ページの料金表も同じ）。工程表のオプションの名前は製品ごと（{pw:scheduleOption}）。申込・解約は #69 */}
      <section className="pp-card" aria-labelledby="plan-options" data-testid="plan-options">
        <div className="pp-sechead">
          <h2 id="plan-options">{M.options}</h2>
          <span className="sp" />
          <span className="pp-secnote">{M.optionsNote}</span>
        </div>
        <div className="pp-opts" role="table" aria-labelledby="plan-options">
          {page.options.stock
            ? page.options.stock.tiers.map((tier, i) => (
                // 在庫は段の記録がまだ無い（E6）＝利用中なら最初の段に出す
                <OptionRow key={tier.key} name={M.optionStock(yen(tier.items))} exTax={tier.priceExTax} inTax={tier.priceInTax} state={i === 0 ? (st.stock ?? "off") : "off"} testId={`plan-option-stock-${tier.key}`} />
              ))
            : null}
          <OptionRow name={M.optionPaper} exTax={page.options.paperDesign.priceExTax} inTax={page.options.paperDesign.priceInTax} state={st.paperDesign} testId="plan-option-paper" />
          {page.options.schedule ? (
            <OptionRow name={fillProduct(SCHEDULE_MSG.optionName, page.product)} exTax={page.options.schedule.priceExTax} inTax={page.options.schedule.priceInTax} state={st.schedule ?? "off"} testId="plan-option-schedule" />
          ) : null}
          <OptionRow
            name={RECEIVED_READ_MSG.optionName}
            exTax={page.options.docRead.priceExTax}
            inTax={page.options.docRead.priceInTax}
            state={st.docRead}
            help={<Help testId="help-plan-doc-read" text={t(RECEIVED_READ_MSG.optionHelp.replace("{n}", String(cur.readsPerMonth)))} />}
            testId="plan-option-doc-read"
          />
        </div>
        <p className="pp-note pp-opts-foot">{M.optionsFoot}</p>
      </section>

      <p className="pp-foot">{withLink(M.foot, "legal", <Link to="/legal">{M.legal}</Link>)}</p>
      {node}
    </div>
  );
}
